import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, MessageSquare, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccessGate from "@/components/AccessGate";

export default function Messages() {
  const { auth } = useAuth();
  const { otherId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const uid = auth?.user?.user_id;

  const fetchThreads = useCallback(async () => {
    try { const r = await api.get("/messages/threads"); setThreads(r.data); } catch (e) { console.error("Failed to load threads:", e); }
  }, []);

  const fetchThread = useCallback(async () => {
    if (!otherId) { setThread(null); return; }
    try { const r = await api.get(`/messages/${otherId}`); setThread(r.data); } catch (e) { console.error("Failed to load thread:", e); }
  }, [otherId]);

  useEffect(() => { if (auth?.has_access) fetchThreads(); }, [fetchThreads, auth]);
  useEffect(() => { if (auth?.has_access) fetchThread(); }, [fetchThread, auth]);
  useEffect(() => {
    if (!auth?.has_access) return;
    const i = setInterval(() => { fetchThreads(); fetchThread(); }, 5000);
    return () => clearInterval(i);
  }, [fetchThreads, fetchThread, auth]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !otherId) return;
    try {
      await api.post("/messages", { receiver_id: otherId, text: text.trim() });
      setText("");
      fetchThread(); fetchThreads();
    } catch (e) { console.error("Failed to send message:", e); }
  };

  return (
    <AccessGate>
      <div className="max-w-6xl">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-6">Messages</h1>
        <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[420px]">
          {/* Threads list */}
          <div className={`rounded-xl border border-border bg-card/40 overflow-y-auto ${otherId ? "hidden md:block" : ""}`}>
            {threads.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No conversations yet. Message a poster from the boards.</div>
            ) : threads.map((t) => (
              <button key={t.other_user_id} data-testid="thread-item" onClick={() => navigate(`/app/messages/${t.other_user_id}`)}
                className={`w-full text-left flex items-center gap-3 p-3.5 border-b border-border/60 hover:bg-secondary/60 transition-colors ${otherId === t.other_user_id ? "bg-secondary" : ""}`}>
                <Avatar className="w-9 h-9 border border-border"><AvatarImage src={t.picture} /><AvatarFallback className="text-xs bg-secondary">{t.name?.[0] || "U"}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.last_message}</p>
                </div>
                {t.unread > 0 && <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-mono-num">{t.unread}</span>}
              </button>
            ))}
          </div>

          {/* Thread view */}
          <div className="rounded-xl border border-border bg-card/40 flex flex-col">
            {!otherId ? (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
                <MessageSquare className="w-10 h-10 opacity-40" />
                <p className="text-sm">Select a conversation to start chatting.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-3.5 border-b border-border">
                  <button className="md:hidden" onClick={() => navigate("/app/messages")}><ArrowLeft className="w-5 h-5" /></button>
                  <Avatar className="w-9 h-9 border border-border"><AvatarImage src={thread?.other?.picture} /><AvatarFallback className="text-xs bg-secondary">{thread?.other?.name?.[0] || "U"}</AvatarFallback></Avatar>
                  <div>
                    <p className="text-sm font-medium">{thread?.other?.name}</p>
                    {thread?.other?.company && <p className="text-xs text-muted-foreground">{thread.other.company}</p>}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                  {thread?.messages?.map((m) => {
                    const mine = m.sender_id === uid;
                    return (
                      <div key={m.id} data-testid="message-bubble" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                          {m.text}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <div className="p-3 border-t border-border flex gap-2">
                  <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="rounded-full" />
                  <Button data-testid="send-message-btn" onClick={send} size="icon" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"><Send className="w-4 h-4" /></Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AccessGate>
  );
}
